<template>
  <div class="flex items-center max-w-full">
    <div
      class="flex-auto w-full flex overflow-x-auto overflow-y-visible py-[2px] flex-col-reverse md:flex-row-reverse justify-between md:items-end md:border-b md:border-neutral-200"
      :class="compact ? 'gap-x-0 gap-y-8' : 'gap-8'"
      v-bind="$attrs"
    >
      <!-- Bottom padding keeps the actions clear of the desktop separator; the tab strip
           stays flush so the active-tab underline still meets the bar. -->
      <div class="md:pb-2">
        <slot name="actions" />
      </div>
      <!-- Desktop: the separator lives on the outer wrapper so it runs BELOW the actions
           buttons instead of cutting through them; the strip keeps it on mobile where
           tabs and actions stack. Tabs wrap onto multiple rows rather than scrolling. -->
      <div
        class="flex items-center flex-wrap gap-y-1 border-b border-neutral-200 md:border-b-0 flex-auto"
        :class="compact ? 'gap-x-1 min-w-0' : 'gap-x-2'"
      >
        <template v-for="item in items" :key="item.value">
          <NuxtLink
            v-if="item.to"
            :to="item.to"
            :replace="noHistory"
            class="flex items-center justify-center pb-1 relative font-medium cursor-pointer hover:text-primary-700 nav-item group shrink-0 no-underline text-inherit"
            :class="{
              'active top-[2px] border-b-[3px] border-primary-700': isActive(item.value),
              'text-typography-disabled': !isActive(item.value),
            }"
            @click.exact="changeTab(item)"
          >
            <div
              class="flex items-center py-2 rounded-md nav-item-text hover:bg-neutral-200 text-xsm"
              :class="{
                'text-primary-700': isActive(item.value),
                'gap-1.5 px-1.5': compact,
                'gap-2 px-2': !compact,
              }"
              :data-testid="`tab-${item.value}`"
            >
              <slot :name="`tab-${item.value}`" :item="item" :active="isActive(item.value)">
                {{ item.label }}
              </slot>
              <div
                v-if="hasSlotContent(slots[`item-${item.value}`])"
                class="nav-item-num text-xsm rounded-md items-center justify-center py-[2px] flex"
                :class="{
                  'px-[5px]': compact,
                  'px-[6px]': !compact,
                  'text-primary-700 bg-primary-100 group-hover:bg-neutral-300': isActive(item.value),
                  'bg-neutral-200 hover:text-primary-700 group-hover:bg-neutral-300': !isActive(item.value),
                }"
              >
                <slot :name="`item-${item.value}`" />
              </div>
            </div>
          </NuxtLink>
          <div
            v-else
            class="flex items-center justify-center pb-1 relative font-medium cursor-pointer hover:text-primary-700 nav-item group shrink-0"
            :class="{
              'active top-[2px] border-b-[3px] border-primary-700': isActive(item.value),
              'text-typography-disabled': !isActive(item.value),
            }"
            @click="changeTab(item)"
          >
            <div
              class="flex items-center py-2 rounded-md nav-item-text hover:bg-neutral-200 text-xsm"
              :class="{
                'text-primary-700': isActive(item.value),
                'gap-1.5 px-1.5': compact,
                'gap-2 px-2': !compact,
              }"
              :data-testid="`tab-${item.value}`"
            >
              <slot :name="`tab-${item.value}`" :item="item" :active="isActive(item.value)">
                {{ item.label }}
              </slot>
              <div
                v-if="hasSlotContent(slots[`item-${item.value}`])"
                class="nav-item-num text-xsm rounded-md items-center justify-center py-[2px] flex"
                :class="{
                  'px-[5px]': compact,
                  'px-[6px]': !compact,
                  'text-primary-700 bg-primary-100 group-hover:bg-neutral-300': isActive(item.value),
                  'bg-neutral-200 hover:text-primary-700 group-hover:bg-neutral-300': !isActive(item.value),
                }"
              >
                <slot :name="`item-${item.value}`" />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
  <slot name="sub-header" />

  <div
    v-if="!stripOnly"
    ref="content"
    style="overflow: auto"
    class="scrollbar-hidden relative h-full"
    :class="{ hidden: loading }"
  >
    <div class="pt-8">
      <slot />
    </div>
  </div>

  <div v-if="loading" class="flex items-center justify-center h-[300px]">
    <PulseLoadingSpinner />
  </div>
</template>

<script setup lang="ts">
import { useCheckSlotsAreEmpty } from '~/support/utils/is-slot-empty';

type NavItem = { label: string; value: string; to?: string };

const slots = useSlots();

const props = defineProps({
  active: {
    default: 'new',
    type: String,
  },
  /** Tightens tab spacing and drops the actions gap so a full strip fits a narrow container; assumes no actions slot. */
  compact: {
    default: false,
    type: Boolean,
  },
  heightAdjust: {
    default: 0,
    type: Number,
  },
  items: {
    default: () => [],
    required: true,
    type: Array as PropType<NavItem[]>,
  },
  loading: {
    default: false,
    type: Boolean,
  },
  noHistory: {
    default: false,
    type: Boolean,
  },
  /** Renders only the tab strip, for a page that lays out its own content below the tabs. */
  stripOnly: {
    default: false,
    type: Boolean,
  },
});

const { hasSlotContent } = useCheckSlotsAreEmpty(slots);

const emit = defineEmits(['nav:click']);

const currentTab = computed(() => {
  return props.active;
});

const isActive = (value: string) => {
  return currentTab.value === value;
};

const content = ref<HTMLElement | null>(null);

// When `item.to` is set the anchor handles navigation natively (supports
// cmd/ctrl-click new-tab semantics). This hook only manages scroll reset + emit.
function changeTab(item: NavItem) {
  content.value && (content.value.scrollTop = 0);
  emit('nav:click', item.value);
}

function handleResize() {
  if (!content.value) return;
  content.value.style.height = `${window.innerHeight - 170 + props.heightAdjust}px`;
}

onMounted(() => {
  window.addEventListener('resize', handleResize);
  handleResize();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});

watch(
  () => props.active,
  newVal => {
    handleResize();
  },
);
</script>

<style lang="scss" scoped>
.scrollbar-hidden {
  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
